import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
    LogOut, Loader2, BookOpen, Target, Flame, ChevronRight, 
    Brain, CheckCircle, Clock, ArrowRight, Gauge, Layers, FileText,
    AlertTriangle, Radar, FileSignature, TreePine, Scan, Award,
    ListChecks, Scale, Filter, Crosshair, Zap, TrendingUp,
    SplitSquareVertical, Radio, RefreshCcw, HandMetal, Cog, FileCheck,
    Search, Minimize2, Layout, Play, Settings2,
    Eye, Link2, SlidersHorizontal, BarChart3, RefreshCw,
    ClipboardCheck, GitBranch, Repeat2, Map,
    ShieldCheck, RotateCcw, Shield, Gift, Cpu,
    Waypoints, Share2, Compass, Fingerprint
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UsuarioDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchProfile = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/user/profile`, {
                withCredentials: true
            });
            setProfile(data);
        } catch (error) {
            toast.error('Error al cargar perfil');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const modules = [
        {
            id: 'diferencial_inicial',
            title: 'Diferencial inicial de procrastinación',
            description: 'Distingue la procrastinación de otros estados internos y reduce el autoetiquetado generalizado.',
            path: '/usuario/modulo/diferencial',
            icon: Target,
            chapter: 1
        },
        {
            id: 'registro_episodios',
            title: 'Registro de episodios',
            description: 'Captura episodios de evitación repetidos e identifica patrones de comportamiento.',
            path: '/usuario/modulo/episodios',
            icon: BookOpen,
            chapter: 1
        },
        {
            id: 'semaforo_funcional',
            title: 'Semáforo funcional',
            description: 'Clasifica tu estado interno dominante para reconocer tu situación con claridad.',
            path: '/usuario/modulo/semaforo',
            icon: Gauge,
            chapter: 1
        },
        {
            id: 'doble_capa',
            title: 'Doble capa de tarea',
            description: 'Identifica la exigencia oculta detrás de la tarea que evitas.',
            path: '/usuario/modulo/doble-capa',
            icon: Layers,
            chapter: 1
        },
        {
            id: 'linea_base',
            title: 'Línea base SENDA',
            description: 'Consolida los datos del capítulo en un perfil conductual estructurado.',
            path: '/usuario/modulo/linea-base',
            icon: FileText,
            chapter: 1
        }
    ];

    const chapter2Modules = [
        {
            id: 'mapa_amenaza',
            title: 'Mapa de amenaza central',
            description: 'Identifica la amenaza interna que se activa cuando enfrentas tareas importantes.',
            path: '/usuario/modulo/mapa-amenaza',
            icon: AlertTriangle,
            chapter: 2
        },
        {
            id: 'radar_exigencia',
            title: 'Radar de exigencia defensiva',
            description: 'Evalúa si tu perfeccionismo te impulsa o te paraliza.',
            path: '/usuario/modulo/radar-exigencia',
            icon: Radar,
            chapter: 2
        },
        {
            id: 'contrato_oculto',
            title: 'Contrato oculto de rendimiento',
            description: 'Descubre el contrato no escrito que llevas contigo sobre lo que debes demostrar.',
            path: '/usuario/modulo/contrato-oculto',
            icon: FileSignature,
            chapter: 2
        },
        {
            id: 'arbol_temor',
            title: 'Árbol de temor raíz',
            description: 'Explora el temor profundo detrás de tu evitación mediante una cadena descendente.',
            path: '/usuario/modulo/arbol-temor',
            icon: TreePine,
            chapter: 2
        },
        {
            id: 'radiografia',
            title: 'Radiografía núcleo-amenaza-defensa',
            description: 'Integra todo lo descubierto en un perfil consolidado de avance.',
            path: '/usuario/modulo/radiografia',
            icon: Scan,
            chapter: 2
        }
    ];

    const chapter3Modules = [
        {
            id: 'inventario_frentes',
            title: 'Inventario de frentes abiertos',
            description: 'Haz visible la fragmentación atencional: mapea todos tus frentes activos.',
            path: '/usuario/modulo/inventario-frentes',
            icon: ListChecks,
            chapter: 3
        },
        {
            id: 'deber_prestado',
            title: 'Detector de deber prestado',
            description: 'Clasifica tus tareas por origen motivacional y detecta carga por culpa o imagen.',
            path: '/usuario/modulo/deber-prestado',
            icon: Scale,
            chapter: 3
        },
        {
            id: 'filtro_impacto',
            title: 'Filtro de impacto real',
            description: 'Diferencia la actividad del progreso real y detecta pseudoavance.',
            path: '/usuario/modulo/filtro-impacto',
            icon: Filter,
            chapter: 3
        },
        {
            id: 'tarea_faro_3',
            title: 'Selección de tarea faro',
            description: 'Selecciona la tarea de mayor impacto para ejecución enfocada.',
            path: '/usuario/modulo/tarea-faro-3',
            icon: Crosshair,
            chapter: 3
        },
        {
            id: 'dai',
            title: 'Acción inevitable (DAI)',
            description: 'Convierte tu tarea faro en un paso concreto con hora, lugar y compromiso.',
            path: '/usuario/modulo/dai',
            icon: Zap,
            chapter: 3
        }
    ];

    const chapter4Modules = [
        {
            id: 'descomposicion_momento',
            title: 'Descomposición del momento',
            description: 'Separa situación, pensamiento, emoción y conducta de un episodio de evitación.',
            path: '/usuario/modulo/descomposicion-momento',
            icon: SplitSquareVertical,
            chapter: 4
        },
        {
            id: 'microsenales',
            title: 'Mapa de microseñales',
            description: 'Identifica las primeras señales internas antes de la evitación.',
            path: '/usuario/modulo/microsenales',
            icon: Radio,
            chapter: 4
        },
        {
            id: 'reescritura',
            title: 'Reescritura sin juicio',
            description: 'Transforma tu lenguaje interno de juicioso a descriptivo y neutral.',
            path: '/usuario/modulo/reescritura',
            icon: RefreshCcw,
            chapter: 4
        },
        {
            id: 'intervencion_vivo',
            title: 'Intervención en vivo',
            description: 'Aplica la secuencia Pausa-Nombre-Elección en situaciones reales.',
            path: '/usuario/modulo/intervencion-vivo',
            icon: HandMetal,
            chapter: 4
        },
        {
            id: 'automatizacion',
            title: 'Automatización de intervención',
            description: 'Diseña un plan SI-ENTONCES para convertir tu intervención en hábito.',
            path: '/usuario/modulo/automatizacion',
            icon: Cog,
            chapter: 4
        }
    ];

    const chapter5Modules = [
        {
            id: 'detector_friccion',
            title: 'Detector de fricción',
            description: 'Identifica las fricciones que te impiden empezar e identifica tu patrón.',
            path: '/usuario/modulo/detector-friccion',
            icon: Search,
            chapter: 5
        },
        {
            id: 'vmv',
            title: 'Versión mínima viable',
            description: 'Reduce tu tarea al mínimo ejecutable en 10 minutos o menos.',
            path: '/usuario/modulo/vmv',
            icon: Minimize2,
            chapter: 5
        },
        {
            id: 'diseno_entorno',
            title: 'Diseño de entorno',
            description: 'Optimiza tu espacio para que ejecutar sea más fácil que no hacerlo.',
            path: '/usuario/modulo/diseno-entorno',
            icon: Layout,
            chapter: 5
        },
        {
            id: 'arranque_obligatorio',
            title: 'Arranque obligatorio',
            description: 'Crea un disparador automático que elimine la necesidad de motivación.',
            path: '/usuario/modulo/arranque-obligatorio',
            icon: Play,
            chapter: 5
        },
        {
            id: 'sistema_ejecucion',
            title: 'Sistema de ejecución',
            description: 'Integra todos los elementos en una secuencia coherente de acción.',
            path: '/usuario/modulo/sistema-ejecucion',
            icon: Settings2,
            chapter: 5
        }
    ];

    const chapter6Modules = [
        {
            id: 'registro_accion',
            title: 'Registro de acción visible',
            description: 'Captura evidencia concreta de ejecución real.',
            path: '/usuario/modulo/registro-accion',
            icon: Eye,
            chapter: 6
        },
        {
            id: 'cadena_ejecucion',
            title: 'Cadena de ejecución',
            description: 'Construye tracción con acciones consecutivas diarias.',
            path: '/usuario/modulo/cadena-ejecucion',
            icon: Link2,
            chapter: 6
        },
        {
            id: 'escala_ajuste',
            title: 'Escala de ajuste',
            description: 'Define tres niveles de intensidad para nunca abandonar.',
            path: '/usuario/modulo/escala-ajuste',
            icon: SlidersHorizontal,
            chapter: 6
        },
        {
            id: 'metricas_funcionales',
            title: 'Métricas funcionales',
            description: 'Define métricas simples que refuercen tu acción.',
            path: '/usuario/modulo/metricas-funcionales',
            icon: BarChart3,
            chapter: 6
        },
        {
            id: 'ciclo_progreso',
            title: 'Ciclo de progreso',
            description: 'Construye un ciclo continuo de acción, medición y ajuste.',
            path: '/usuario/modulo/ciclo-progreso',
            icon: RefreshCw,
            chapter: 6
        }
    ];

    const chapter7Modules = [
        {
            id: 'evaluacion_funcional',
            title: 'Evaluación funcional',
            description: 'Evalúa resultados sin juicio personal, con objetividad.',
            path: '/usuario/modulo/evaluacion-funcional',
            icon: ClipboardCheck,
            chapter: 7
        },
        {
            id: 'mapa_patrones',
            title: 'Mapa de patrones',
            description: 'Identifica secuencias conductuales recurrentes.',
            path: '/usuario/modulo/mapa-patrones',
            icon: GitBranch,
            chapter: 7
        },
        {
            id: 'punto_intervencion',
            title: 'Punto de intervención',
            description: 'Elige el punto de palanca más efectivo para el cambio.',
            path: '/usuario/modulo/punto-intervencion',
            icon: Crosshair,
            chapter: 7
        },
        {
            id: 'rediseno_patron',
            title: 'Rediseño de patrón',
            description: 'Reemplaza tu patrón actual por uno más funcional.',
            path: '/usuario/modulo/rediseno-patron',
            icon: Repeat2,
            chapter: 7
        },
        {
            id: 'mapa_decision',
            title: 'Mapa de decisión',
            description: 'Convierte tu experiencia en reglas operativas claras.',
            path: '/usuario/modulo/mapa-decision',
            icon: Map,
            chapter: 7
        }
    ];

    const chapter8Modules = [
        {
            id: 'diagnostico_sostenibilidad',
            title: 'Revisión de sostenibilidad',
            description: 'Evalúa si tu sistema actual puede mantenerse en el tiempo.',
            path: '/usuario/modulo/diagnostico-sostenibilidad',
            icon: ShieldCheck,
            chapter: 8
        },
        {
            id: 'reinicio_rapido',
            title: 'Reinicio rápido',
            description: 'Define un protocolo de reinicio inmediato tras una interrupción.',
            path: '/usuario/modulo/reinicio-rapido',
            icon: RotateCcw,
            chapter: 8
        },
        {
            id: 'proteccion_recaidas',
            title: 'Protección contra recaídas',
            description: 'Prevé la escalada detectando riesgos temprano.',
            path: '/usuario/modulo/proteccion-recaidas',
            icon: Shield,
            chapter: 8
        },
        {
            id: 'refuerzo_inteligente',
            title: 'Refuerzo inteligente',
            description: 'Aumenta la repetición mediante refuerzo inmediato.',
            path: '/usuario/modulo/refuerzo-inteligente',
            icon: Gift,
            chapter: 8
        },
        {
            id: 'sistema_autonomo',
            title: 'Sistema autónomo',
            description: 'Integra todo en un sistema autosuficiente.',
            path: '/usuario/modulo/sistema-autonomo',
            icon: Cpu,
            chapter: 8
        }
    ];

    const chapter9Modules = [
        {
            id: 'senales_integracion',
            title: 'Señales de integración',
            description: 'Identifica cambio conductual consolidado con evidencia concreta.',
            path: '/usuario/modulo/senales-integracion',
            icon: Waypoints,
            chapter: 9
        },
        {
            id: 'transferencia_sistema',
            title: 'Transferencia del sistema',
            description: 'Aplica tu sistema SENDA a contextos nuevos.',
            path: '/usuario/modulo/transferencia-sistema',
            icon: Share2,
            chapter: 9
        },
        {
            id: 'criterio_propio',
            title: 'Criterio propio',
            description: 'Desarrolla autonomía decisional interna.',
            path: '/usuario/modulo/criterio-propio',
            icon: Compass,
            chapter: 9
        },
        {
            id: 'identidad_operativa',
            title: 'Identidad operativa',
            description: 'Consolida una identidad basada en comportamiento real.',
            path: '/usuario/modulo/identidad-operativa',
            icon: Fingerprint,
            chapter: 9
        },
        {
            id: 'vision_operativa',
            title: 'Visión operativa',
            description: 'Define tu sistema operativo personal para el futuro.',
            path: '/usuario/modulo/vision-operativa',
            icon: Eye,
            chapter: 9
        }
    ];

    const isModuleCompleted = (moduleId) => {
        return profile?.modules_completed?.some(m => m.module_id === moduleId);
    };

    const isModuleAvailable = (moduleId) => {
        // Chapter 1 unlock logic
        if (moduleId === 'diferencial_inicial') return true;
        if (moduleId === 'registro_episodios') return isModuleCompleted('diferencial_inicial');
        if (moduleId === 'semaforo_funcional') return isModuleCompleted('registro_episodios');
        if (moduleId === 'doble_capa') return isModuleCompleted('semaforo_funcional');
        if (moduleId === 'linea_base') return isModuleCompleted('doble_capa');
        // Chapter 2 unlock logic (requires Chapter 1 complete)
        if (moduleId === 'mapa_amenaza') return isModuleCompleted('linea_base');
        if (moduleId === 'radar_exigencia') return isModuleCompleted('mapa_amenaza');
        if (moduleId === 'contrato_oculto') return isModuleCompleted('radar_exigencia');
        if (moduleId === 'arbol_temor') return isModuleCompleted('contrato_oculto');
        if (moduleId === 'radiografia') return isModuleCompleted('arbol_temor');
        // Chapter 3 unlock logic (requires Chapter 2 complete)
        if (moduleId === 'inventario_frentes') return isModuleCompleted('radiografia');
        if (moduleId === 'deber_prestado') return isModuleCompleted('inventario_frentes');
        if (moduleId === 'filtro_impacto') return isModuleCompleted('deber_prestado');
        if (moduleId === 'tarea_faro_3') return isModuleCompleted('filtro_impacto');
        if (moduleId === 'dai') return isModuleCompleted('tarea_faro_3');
        // Chapter 4 unlock logic (requires Chapter 3 complete)
        if (moduleId === 'descomposicion_momento') return isModuleCompleted('dai');
        if (moduleId === 'microsenales') return isModuleCompleted('descomposicion_momento');
        if (moduleId === 'reescritura') return isModuleCompleted('microsenales');
        if (moduleId === 'intervencion_vivo') return isModuleCompleted('reescritura');
        if (moduleId === 'automatizacion') return isModuleCompleted('intervencion_vivo');
        // Chapter 5 unlock logic (requires Chapter 4 complete)
        if (moduleId === 'detector_friccion') return isModuleCompleted('automatizacion');
        if (moduleId === 'vmv') return isModuleCompleted('detector_friccion');
        if (moduleId === 'diseno_entorno') return isModuleCompleted('vmv');
        if (moduleId === 'arranque_obligatorio') return isModuleCompleted('diseno_entorno');
        if (moduleId === 'sistema_ejecucion') return isModuleCompleted('arranque_obligatorio');
        // Chapter 6 unlock logic (requires Chapter 5 complete)
        if (moduleId === 'registro_accion') return isModuleCompleted('sistema_ejecucion');
        if (moduleId === 'cadena_ejecucion') return isModuleCompleted('registro_accion');
        if (moduleId === 'escala_ajuste') return isModuleCompleted('cadena_ejecucion');
        if (moduleId === 'metricas_funcionales') return isModuleCompleted('escala_ajuste');
        if (moduleId === 'ciclo_progreso') return isModuleCompleted('metricas_funcionales');
        // Chapter 7 unlock logic (requires Chapter 6 complete)
        if (moduleId === 'evaluacion_funcional') return isModuleCompleted('ciclo_progreso');
        if (moduleId === 'mapa_patrones') return isModuleCompleted('evaluacion_funcional');
        if (moduleId === 'punto_intervencion') return isModuleCompleted('mapa_patrones');
        if (moduleId === 'rediseno_patron') return isModuleCompleted('punto_intervencion');
        if (moduleId === 'mapa_decision') return isModuleCompleted('rediseno_patron');
        // Chapter 8 unlock logic (requires Chapter 7 complete)
        if (moduleId === 'diagnostico_sostenibilidad') return isModuleCompleted('mapa_decision');
        if (moduleId === 'reinicio_rapido') return isModuleCompleted('diagnostico_sostenibilidad');
        if (moduleId === 'proteccion_recaidas') return isModuleCompleted('reinicio_rapido');
        if (moduleId === 'refuerzo_inteligente') return isModuleCompleted('proteccion_recaidas');
        if (moduleId === 'sistema_autonomo') return isModuleCompleted('refuerzo_inteligente');
        // Chapter 9 unlock logic (requires Chapter 8 complete)
        if (moduleId === 'senales_integracion') return isModuleCompleted('sistema_autonomo');
        if (moduleId === 'transferencia_sistema') return isModuleCompleted('senales_integracion');
        if (moduleId === 'criterio_propio') return isModuleCompleted('transferencia_sistema');
        if (moduleId === 'identidad_operativa') return isModuleCompleted('criterio_propio');
        if (moduleId === 'vision_operativa') return isModuleCompleted('identidad_operativa');
        return false;
    };

    const isChapter1Complete = modules.every(m => isModuleCompleted(m.id));
    const isChapter2Complete = chapter2Modules.every(m => isModuleCompleted(m.id));
    const isChapter3Complete = chapter3Modules.every(m => isModuleCompleted(m.id));
    const isChapter4Complete = chapter4Modules.every(m => isModuleCompleted(m.id));
    const isChapter5Complete = chapter5Modules.every(m => isModuleCompleted(m.id));
    const isChapter6Complete = chapter6Modules.every(m => isModuleCompleted(m.id));
    const isChapter7Complete = chapter7Modules.every(m => isModuleCompleted(m.id));
    const isChapter8Complete = chapter8Modules.every(m => isModuleCompleted(m.id));
    const isChapter9Complete = chapter9Modules.every(m => isModuleCompleted(m.id));

    const latestInsight = profile?.ai_summaries?.[profile.ai_summaries.length - 1]?.insight_usuario;

    if (loading) {
        return (
            <div className="min-h-screen bg-[#FBFBF9] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#425A65]" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FBFBF9]">
            <Toaster position="top-right" richColors />
            
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-xl border-b border-[#EAE8E3]/50 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                            <img src="/logo-senda.png" alt="SENDA" className="h-8 sm:h-9 w-auto shrink-0" />
                            <div className="min-w-0">
                                <h1 className="text-lg sm:text-xl font-semibold text-[#1F2933] font-['Outfit'] truncate">Mi espacio</h1>
                                <p className="text-xs sm:text-sm text-[#616E7C] truncate">Hola, {user?.name || profile?.name}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                            <Link to="/usuario/mis-informes">
                                <Button variant="outline" size="sm" className="border-[#3E5F6E]/20 text-[#3E5F6E] hover:bg-[#3E5F6E]/5 px-2 sm:px-4" data-testid="informes-link">
                                    <FileCheck className="h-4 w-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Informes</span>
                                </Button>
                            </Link>
                            <Link to="/usuario/evolucion">
                                <Button variant="outline" size="sm" className="border-[#3E5F6E]/20 text-[#3E5F6E] hover:bg-[#3E5F6E]/5 px-2 sm:px-4" data-testid="evolucion-link">
                                    <TrendingUp className="h-4 w-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Mi evolución</span>
                                </Button>
                            </Link>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/download`} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="border-[#5FAF9D]/30 text-[#5FAF9D] hover:bg-[#5FAF9D]/5 px-2 sm:px-4" data-testid="manual-link">
                                    <BookOpen className="h-4 w-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Manual de uso</span>
                                </Button>
                            </a>
                            <Button 
                                onClick={handleLogout} 
                                variant="ghost" 
                                size="sm"
                                className="senda-btn-ghost px-2 sm:px-4"
                                data-testid="usuario-logout-btn"
                            >
                                <LogOut className="h-4 w-4 sm:mr-2" />
                                <span className="hidden sm:inline">Salir</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Progress Overview */}
                <Card className="senda-card mb-8 animate-fade-in-up">
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex-1">
                                <h2 className="senda-heading-3 mb-2">Tu progreso</h2>
                                <p className="senda-body mb-4">
                                    Continúa tu camino hacia el autoconocimiento
                                </p>
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm text-[#616E7C]">Progreso total</span>
                                            <span className="text-sm font-medium text-[#1F2933]">{Math.min(100, Math.round(((profile?.modules_completed || []).filter((m, i, arr) => arr.findIndex(x => x.module_id === m.module_id) === i).length / 45) * 100))}%</span>
                                        </div>
                                        <Progress value={Math.min(100, Math.round(((profile?.modules_completed || []).filter((m, i, arr) => arr.findIndex(x => x.module_id === m.module_id) === i).length / 45) * 100))} className="h-3" />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-6 md:border-l md:border-[#EAE8E3] md:pl-6">
                                <div className="text-center">
                                    <div className="w-14 h-14 bg-[#E8B27A]/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                                        <Flame className="h-7 w-7 text-[#E8B27A]" />
                                    </div>
                                    <p className="text-2xl font-semibold text-[#1F2933] font-['Outfit']">{profile?.streak_days || 0}</p>
                                    <p className="text-xs text-[#9AA5B1]">Días de racha</p>
                                </div>
                                <div className="text-center">
                                    <div className="w-14 h-14 bg-[#7F9C8B]/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                                        <CheckCircle className="h-7 w-7 text-[#7F9C8B]" />
                                    </div>
                                    <p className="text-2xl font-semibold text-[#1F2933] font-['Outfit']">{profile?.modules_completed?.length || 0}</p>
                                    <p className="text-xs text-[#9AA5B1]">Módulos</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* AI Insight (if available) */}
                {latestInsight && (
                    <Card className="senda-card mb-8 animate-fade-in-up animate-stagger-1">
                        <CardContent className="pt-6">
                            <div className="senda-ai-panel">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-[#A29DB0]/20 rounded-full flex items-center justify-center shrink-0">
                                        <Brain className="h-5 w-5 text-[#A29DB0]" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-[#A29DB0] uppercase mb-2">Reflexión para ti</p>
                                        <p className="text-[#1F2933] italic">"{latestInsight}"</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 1 Modules */}
                <h3 className="senda-heading-3 mb-4">Capítulo 1: Autoexploración</h3>
                <div className="space-y-4">
                    {modules.map((module, index) => {
                        const completed = isModuleCompleted(module.id);
                        const available = isModuleAvailable(module.id);
                        const Icon = module.icon;
                        
                        return (
                            <Card 
                                key={module.id} 
                                className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                data-testid={`module-card-${module.id}`}
                            >
                                <CardContent className="pt-4 sm:pt-6">
                                    <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                        <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                            completed ? 'bg-[#7F9C8B]' : available ? 'bg-[#425A65]' : 'bg-[#EAE8E3]'
                                        }`}>
                                            {completed ? (
                                                <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                            ) : (
                                                <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                            )}
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                {completed && (
                                                    <Badge className="senda-status-success text-xs">Completado</Badge>
                                                )}
                                                {!available && !completed && (
                                                    <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                        <Clock className="h-3 w-3 mr-1" />
                                                        Bloqueado
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                            
                                            <div className="mt-2 sm:hidden">
                                                {available && !completed && (
                                                    <Link to={module.path}>
                                                        <Button size="sm" className="senda-btn-primary" data-testid={`start-module-${module.id}`}>
                                                            Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                        </Button>
                                                    </Link>
                                                )}
                                                {completed && (
                                                    <Link to={module.path}>
                                                        <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                            Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                        </Button>
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="hidden sm:block">
                                            {available && !completed && (
                                                <Link to={module.path}>
                                                    <Button className="senda-btn-primary shrink-0" data-testid={`start-module-${module.id}`}>
                                                        Comenzar
                                                        <ArrowRight className="h-4 w-4 ml-2" />
                                                    </Button>
                                                </Link>
                                            )}
                                            
                                            {completed && (
                                                <Link to={module.path}>
                                                    <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                        Revisar
                                                        <ChevronRight className="h-4 w-4 ml-1" />
                                                    </Button>
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Chapter 1 Summary CTA */}
                {isChapter1Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#425A65] to-[#3E5F6E] border-0 shadow-lg animate-fade-in-up" data-testid="chapter1-summary-card">
                        <CardContent className="py-4 sm:py-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#5FAF9D] rounded-xl flex items-center justify-center shrink-0">
                                        <Award className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-sm sm:text-base text-white font-['Outfit']">Resumen Capítulo 1</h4>
                                        <p className="text-xs sm:text-sm text-white/70">Revisa tu perfil conductual consolidado</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-1">
                                    <Button size="sm" className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white" data-testid="view-chapter1-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 2 Modules */}
                {isChapter1Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 2: Amenaza y Defensa</h3>
                        <div className="space-y-4">
                            {chapter2Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#3E5F6E]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 2 Summary CTA */}
                {isChapter2Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] border-0 shadow-lg animate-fade-in-up" data-testid="chapter2-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-[#5FAF9D] rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 2</h4>
                                        <p className="text-sm text-white/70">Tu radiografía integrada SENDA</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-2">
                                    <Button className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white" data-testid="view-chapter2-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 3 Modules */}
                {isChapter2Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 3: Foco y Acción</h3>
                        <div className="space-y-4">
                            {chapter3Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#425A65]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#425A65] hover:bg-[#344750] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#425A65] hover:bg-[#344750] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 3 Summary CTA */}
                {isChapter3Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#5FAF9D] to-[#4A9A89] border-0 shadow-lg animate-fade-in-up" data-testid="chapter3-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 3</h4>
                                        <p className="text-sm text-white/70">Tu plan de acción consolidado</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-3">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white" data-testid="view-chapter3-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 4 Modules */}
                {isChapter3Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 4: Intervención y Automatización</h3>
                        <div className="space-y-4">
                            {chapter4Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#3E5F6E]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 4 Summary CTA */}
                {isChapter4Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#3E5F6E] to-[#1F2A30] border-0 shadow-lg animate-fade-in-up" data-testid="chapter4-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-[#5FAF9D] rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 4</h4>
                                        <p className="text-sm text-white/70">Tu sistema de intervención automática</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-4">
                                    <Button className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white" data-testid="view-chapter4-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 5 Modules */}
                {isChapter4Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 5: Ejecución sin fricción</h3>
                        <div className="space-y-4">
                            {chapter5Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#E8B27A]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#E8B27A] hover:bg-[#D4A06A] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#E8B27A] hover:bg-[#D4A06A] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 5 Summary CTA */}
                {isChapter5Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#E8B27A] to-[#D4A06A] border-0 shadow-lg animate-fade-in-up" data-testid="chapter5-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 5</h4>
                                        <p className="text-sm text-white/70">Tu sistema de ejecución completo</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-5">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white" data-testid="view-chapter5-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 6 Modules */}
                {isChapter5Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 6: Sostenimiento y progreso</h3>
                        <div className="space-y-4">
                            {chapter6Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#5FAF9D]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 6 Summary CTA */}
                {isChapter6Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#5FAF9D] to-[#3E5F6E] border-0 shadow-lg animate-fade-in-up" data-testid="chapter6-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 6</h4>
                                        <p className="text-sm text-white/70">Tu sistema de sostenimiento completo</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-6">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white" data-testid="view-chapter6-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 7 Modules */}
                {isChapter6Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 7: Evaluación y rediseño</h3>
                        <div className="space-y-4">
                            {chapter7Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#7A9BA8]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#7A9BA8] hover:bg-[#5F8494] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#7A9BA8] hover:bg-[#5F8494] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 7 Summary CTA */}
                {isChapter7Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#7A9BA8] to-[#3E5F6E] border-0 shadow-lg animate-fade-in-up" data-testid="chapter7-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 7</h4>
                                        <p className="text-sm text-white/70">Tu sistema de evaluación y rediseño</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-7">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white" data-testid="view-chapter7-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 8 Modules */}
                {isChapter7Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 8: Revisión y sistema autónomo</h3>
                        <div className="space-y-4">
                            {chapter8Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#3E5F6E]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 8 Summary CTA */}
                {isChapter8Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#5FAF9D] to-[#3E5F6E] border-0 shadow-lg animate-fade-in-up" data-testid="chapter8-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Cpu className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 8</h4>
                                        <p className="text-sm text-white/70">Tu sistema autónomo integrado</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-8">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white" data-testid="view-chapter8-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Chapter 9 Modules */}
                {isChapter8Complete && (
                    <>
                        <h3 className="senda-heading-3 mb-4 mt-10">Capítulo 9: Integración y cierre</h3>
                        <div className="space-y-4">
                            {chapter9Modules.map((module, index) => {
                                const completed = isModuleCompleted(module.id);
                                const available = isModuleAvailable(module.id);
                                const Icon = module.icon;
                                
                                return (
                                    <Card 
                                        key={module.id} 
                                        className={`senda-card ${available && !completed ? 'senda-card-hover cursor-pointer' : ''} animate-fade-in-up`}
                                        style={{ animationDelay: `${(index + 2) * 0.1}s` }}
                                        data-testid={`module-card-${module.id}`}
                                    >
                                        <CardContent className="pt-4 sm:pt-6">
                                            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                                                <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${
                                                    completed ? 'bg-[#5FAF9D]' : available ? 'bg-[#3E5F6E]' : 'bg-[#EAE8E3]'
                                                }`}>
                                                    {completed ? (
                                                        <CheckCircle className="h-5 w-5 sm:h-7 sm:w-7 text-white" />
                                                    ) : (
                                                        <Icon className={`h-5 w-5 sm:h-7 sm:w-7 ${available ? 'text-white' : 'text-[#9AA5B1]'}`} />
                                                    )}
                                                </div>
                                                
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                                                        <h4 className="font-semibold text-sm sm:text-base text-[#1F2933] font-['Outfit']">{module.title}</h4>
                                                        {completed && (
                                                            <Badge className="bg-[#5FAF9D]/10 text-[#5FAF9D] text-xs">Completado</Badge>
                                                        )}
                                                        {!available && !completed && (
                                                            <Badge className="bg-[#EAE8E3] text-[#9AA5B1] text-xs">
                                                                <Clock className="h-3 w-3 mr-1" />
                                                                Bloqueado
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-[#616E7C] line-clamp-2">{module.description}</p>
                                                    <div className="mt-2 sm:hidden">
                                                        {available && !completed && (
                                                            <Link to={module.path}>
                                                                <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" data-testid={`start-module-${module.id}`}>
                                                                    Comenzar <ArrowRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                        {completed && (
                                                            <Link to={module.path}>
                                                                <Button variant="ghost" size="sm" className="senda-btn-ghost" data-testid={`review-module-${module.id}`}>
                                                                    Revisar <ChevronRight className="h-3 w-3 ml-1" />
                                                                </Button>
                                                            </Link>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="hidden sm:block">
                                                    {available && !completed && (
                                                        <Link to={module.path}>
                                                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white shrink-0" data-testid={`start-module-${module.id}`}>
                                                                Comenzar <ArrowRight className="h-4 w-4 ml-2" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                    {completed && (
                                                        <Link to={module.path}>
                                                            <Button variant="ghost" className="senda-btn-ghost shrink-0" data-testid={`review-module-${module.id}`}>
                                                                Revisar <ChevronRight className="h-4 w-4 ml-1" />
                                                            </Button>
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* Chapter 9 Summary CTA */}
                {isChapter9Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#5FAF9D] to-[#2F4A57] border-0 shadow-lg animate-fade-in-up" data-testid="chapter9-summary-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Resumen Capítulo 9</h4>
                                        <p className="text-sm text-white/70">Tu proceso SENDA completo</p>
                                    </div>
                                </div>
                                <Link to="/usuario/resumen-capitulo-9">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white" data-testid="view-chapter9-summary">
                                        Ver resumen
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Certificate CTA */}
                {isChapter9Complete && (
                    <Card className="mt-6 bg-gradient-to-r from-[#C4A35A] to-[#D4B96A] border-0 shadow-lg animate-fade-in-up" data-testid="certificate-cta-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/25 rounded-xl flex items-center justify-center">
                                        <Award className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Certificado SENDA</h4>
                                        <p className="text-sm text-white/80">Descarga tu certificado de completación</p>
                                    </div>
                                </div>
                                <Link to="/usuario/certificado">
                                    <Button className="bg-white/25 hover:bg-white/35 text-white font-semibold" data-testid="view-certificate-btn">
                                        Ver certificado
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Plan SENDA 3x3 CTA */}
                {isChapter9Complete && (
                    <Card className="mt-4 bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] border-0 shadow-lg animate-fade-in-up" data-testid="plan3x3-cta-card">
                        <CardContent className="py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center">
                                        <Target className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-white font-['Outfit']">Plan SENDA 3x3</h4>
                                        <p className="text-sm text-white/75">Su sistema de ejecución personalizado</p>
                                    </div>
                                </div>
                                <Link to="/usuario/plan-senda-3x3">
                                    <Button className="bg-white/20 hover:bg-white/30 text-white font-semibold" data-testid="plan3x3-cta-btn">
                                        Acceder
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </main>
            <SENDAFooter />
        </div>
    );
}

